import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const CHAPTERS = [
  {
    number: 1,
    title: 'The Notification',
    path: '/gnul/chapter-1',
    available: true,
    lessons: [
      'The 400-Millisecond Window',
      'Fear Converted to Humor',
      'Remote Oxytocin Formation',
      'The Silence Principle',
      'Vagal Breathing (4-7-8)',
    ],
    homework: [
      'Identify a moment this week where you heard "I\'m fine" but the voice said otherwise. What frequency shift did you detect?',
      'Practice the 4-7-8 breathing technique before your next meeting. Note how it changes your vocal tone.',
      'Observe a Slack or Teams channel for one day. Who is the "Loriann" in your organization? Who defends without being asked?',
    ],
  },
  {
    number: 2,
    title: 'The Terminal',
    path: '/gnul/chapter-2',
    available: true,
    lessons: [
      'Mirror Neuron Cascade',
      'Threat Confirmation Bias',
      'The Concrete Anchor',
      'The Three-Foot Rule',
      'Neurochemical Invisibility',
    ],
    homework: [
      'Walk into a room where you are the outsider. Notice what your body does before your mind catches up. Where does the tension land?',
      'The next time someone is visibly upset, resist "are you okay?" Instead, name something concrete. "Your bag fell." "Your coffee is getting cold." Watch what happens.',
      'Observe someone whose job is to be calm (flight attendant, concierge, receptionist). Listen to their frequency. What makes their voice feel neutral? What is absent?',
    ],
  },
  {
    number: 3,
    title: 'The Landing',
    path: '/gnul/chapter-3',
    available: true,
    lessons: [
      'Bilateral Frequency',
      'Cortisol Half-Life as Leadership Limit',
      'Power Vacuum Dynamics',
      'Uncontaminated Node Value',
      'Information Asymmetry as Strategic Advantage',
    ],
    homework: [
      'Think of a leader you have worked for who used different tones with their superiors vs their team. What was the effect on trust? Could you detect the shift?',
      'Recall a fear-based directive you received at work. How long did the fear last? What replaced it? Was it compliance or resentment?',
      'Identify the "Loriann" in your organization. The person whose calm does not mirror the ambient stress. What makes them different? What would happen if they left?',
    ],
  },
  {
    number: 4,
    title: 'The Meridian',
    path: '/gnul/chapter-4',
    available: true,
    lessons: [
      'Parasympathetic Collapse',
      'The Dive Reflex',
      'Clothing as Frequency Modifier',
      'Environmental Serotonin Design',
      'Anticipatory Care vs. Reactive Care',
    ],
    homework: [
      'Write two lists. What you know about your current challenge. What you do not know. Which list is longer? What does that tell you about where you are in the process?',
      'Notice a space that makes you feel calm. A room, a building, a corner. What specific elements produce that feeling? Lighting? Temperature? Scent? You are reverse-engineering serotonin.',
      'Think of a time someone anticipated your need before you asked. How did that feel compared to getting help after you asked for it? That difference is the oxytocin bonus of anticipatory care.',
    ],
  },
  {
    number: 5,
    title: 'The Lobby',
    path: '/gnul/chapter-5',
    available: true,
    lessons: [
      'Recognition Before Resolution',
      'The Single-Word Answer',
      'Shared Meal as Trust Catalyst',
      'Position as Signal',
      'The Honesty Paradox',
    ],
    homework: [
      'The next time someone on your team is upset, resist solving the problem. Name it instead. "That sounds frustrating." Watch what happens to their shoulders.',
      'Pay attention to where you sit in your next meeting. Head of table? End? Off to the side? Each position sends a signal before you speak. Choose it deliberately.',
      'Think of a leader who admitted they did not know something. How did it change your trust in them? Now think of a leader who never admitted it. Which one do you follow?',
    ],
  },
  {
    number: 6,
    title: 'The Slide Deck',
    path: '/gnul/chapter-6',
    available: true,
    lessons: [
      'The Diagnostic Question',
      'Attention Decay in Fear-Based Audiences',
      'Seating Position as Power Architecture',
      'Corporate Language as Trust Signal',
      'Curiosity Replaces Fear',
    ],
    homework: [
      'In your next meeting, sit in a different seat than usual. Notice how it changes the dynamic. Who speaks to you differently? Who ignores you? Position is a frequency.',
      'Listen for corporate deflection this week. When someone answers a human question with organizational language, notice how it lands in your body. That feeling is trust eroding.',
      'Ask one question this week that names what people are feeling instead of what they are producing. Watch the room change.',
    ],
  },
  {
    number: 7,
    title: 'The Bill',
    path: '/gnul/chapter-7',
    available: true,
    lessons: [
      'The Cost of Fear Leadership',
      'Accountability Inversion',
      'The Corporate Non-Answer',
      'Serotonin as Minimum Viable Dose',
      'Applause as Neurochemical Event',
    ],
    homework: [
      'Think of a decision at your organization that cost others but not the person who made it. What would change if the decision-maker bore the cost personally?',
      'Listen for a corporate non-answer this week. Count the words. Then count how many bits of information were actually conveyed. The ratio is the deflection.',
      'After a high-stress week, give yourself or your team one genuine recovery day. Not a "work from home" day. An actual day off. Notice how the following Monday feels different.',
    ],
  },
  {
    number: 8,
    title: 'The Empty Building',
    path: '/gnul/chapter-8',
    available: true,
    lessons: [
      'Intrinsic vs. Extrinsic Motivation',
      'The Voss Buffer as Hidden Cost',
      'Leadership as Obstacle Removal',
      'The Confirming Signal',
    ],
    homework: [
      'If your entire team had a paid day off tomorrow, who would show up anyway? What does that tell you about where the real motivation lives?',
      'Ask your team: what do you build slack for? What recurring disruption do you silently engineer around? The answer is your hidden cost.',
      'This week, instead of adding a task, remove an obstacle. Cancel one meeting. Eliminate one report. Watch what fills the space.',
    ],
  },
  {
    number: 9,
    title: 'Coming Soon',
    path: '/gnul/chapter-9',
    available: false,
    lessons: [],
    homework: [],
  },
];

function ChapterMenu({ chapter, isActive }) {
  const [storyOpen, setStoryOpen] = useState(isActive);
  const [lessonsOpen, setLessonsOpen] = useState(false);
  const [homeworkOpen, setHomeworkOpen] = useState(false);

  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div
        style={{
          padding: '0.6rem 1rem',
          color: isActive ? '#c9a84c' : chapter.available ? '#ede8df' : '#555',
          fontWeight: 600,
          fontSize: '0.85rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          cursor: chapter.available ? 'pointer' : 'default',
          borderLeft: isActive ? '2px solid #c9a84c' : '2px solid transparent',
          background: isActive ? 'rgba(201,168,76,0.06)' : 'transparent',
        }}
        onClick={() => chapter.available && setStoryOpen(!storyOpen)}
      >
        Chapter {chapter.number}
      </div>

      {storyOpen && chapter.available && (
        <div style={{ paddingLeft: '1rem' }}>
          <Link
            to={chapter.path}
            style={{
              display: 'block',
              padding: '0.4rem 1rem',
              color: isActive ? '#c9a84c' : '#a09484',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.2s',
            }}
          >
            {chapter.title}
          </Link>

          {chapter.lessons.length > 0 && (
            <>
              <div
                onClick={() => setLessonsOpen(!lessonsOpen)}
                style={{
                  padding: '0.4rem 1rem',
                  color: '#a09484',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                <span style={{
                  display: 'inline-block',
                  transform: lessonsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  fontSize: '0.7rem',
                }}>&#9654;</span>
                Lessons
              </div>
              {lessonsOpen && (
                <div style={{ paddingLeft: '1.5rem' }}>
                  {chapter.lessons.map((lesson, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '0.3rem 0.5rem',
                        color: '#6b5f52',
                        fontSize: '0.8rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {lesson}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {chapter.homework.length > 0 && (
            <>
              <div
                onClick={() => setHomeworkOpen(!homeworkOpen)}
                style={{
                  padding: '0.4rem 1rem',
                  color: '#a09484',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                <span style={{
                  display: 'inline-block',
                  transform: homeworkOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  fontSize: '0.7rem',
                }}>&#9654;</span>
                Homework
              </div>
              {homeworkOpen && (
                <div style={{ paddingLeft: '1.5rem' }}>
                  {chapter.homework.map((hw, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '0.3rem 0.5rem',
                        color: '#6b5f52',
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        marginBottom: '0.4rem',
                      }}
                    >
                      {i + 1}. {hw}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function GnulSidebar({ mobileOpen, onClose }) {
  const location = useLocation();

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 998,
          }}
        />
      )}

      <div style={{
        width: '280px',
        minHeight: '100vh',
        background: '#0a0a0a',
        borderRight: '1px solid rgba(201,168,76,0.12)',
        padding: '1.5rem 0',
        overflowY: 'auto',
        flexShrink: 0,
        position: 'fixed',
        top: 0,
        left: mobileOpen ? 0 : '-300px',
        zIndex: 999,
        transition: 'left 0.3s ease',
        '@media (min-width: 769px)': {
          position: 'sticky',
          left: 0,
        },
      }}
      className="gnul-sidebar"
      >
        {/* Close button - mobile only */}
        <button
          onClick={onClose}
          className="gnul-sidebar-close"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            color: '#c9a84c',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.25rem',
            lineHeight: 1,
          }}
        >
          &times;
        </button>

        {/* Header */}
        <Link to="/gnul" style={{ textDecoration: 'none' }} onClick={onClose}>
          <div style={{
            padding: '0 1rem 1.2rem',
            borderBottom: '1px solid rgba(201,168,76,0.12)',
            marginBottom: '1rem',
          }}>
            <div style={{
              color: '#c9a84c',
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '0.08em',
            }}>
              GNUL
            </div>
            <div style={{
              color: '#6b5f52',
              fontSize: '0.75rem',
              marginTop: '0.3rem',
            }}>
              The Chemistry of Leadership
            </div>
          </div>
        </Link>

        {/* Chapters */}
        {CHAPTERS.map((chapter) => (
          <ChapterMenu
            key={chapter.number}
            chapter={chapter}
            isActive={location.pathname === chapter.path}
          />
        ))}

        {/* Footer */}
        <div style={{
          padding: '1.5rem 1rem 0',
          borderTop: '1px solid rgba(201,168,76,0.08)',
          marginTop: '2rem',
        }}>
          <div style={{ color: '#333', fontSize: '0.7rem', lineHeight: 1.5 }}>
            Narrative Physics&trade;<br />
            Leadership Development<br />
            Nexus Concordat Inc.
          </div>
        </div>
      </div>
    </>
  );
}
